import {
  Button, Divider, FormControl, FormLabel, IconButton,
  Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger,
  Portal, Radio, RadioGroup, Stack, Switch, VStack,
} from "@chakra-ui/react";
import { FiSettings } from "react-icons/fi";
import CDS from "design-system";
import type { AutoAddSide } from "../types";

interface Props {
  autoAdd: boolean;
  onAutoAddChange: (v: boolean) => void;
  side: AutoAddSide;
  onSideChange: (v: AutoAddSide) => void;
  /** Count of active journeys currently absent from the view (hidden or
   *  never positioned). Drives the "Add all active journeys" badge. */
  journeysNotInViewCount: number;
  onAddAllJourneys: () => void;
  /** Count of task templates currently absent from the view. Drives the
   *  "Add all task templates" badge. */
  templatesNotInViewCount: number;
  onAddAllTemplates: () => void;
}

/**
 * Settings popover for the task grid. Lives in the page header. Surfaces:
 *  - Auto add new hires toggle (default on)
 *  - Which side new hires land on (left / right)
 *  - "Add all active journeys" reset action — brings every active journey
 *    back into the view (unhides + persists into journey_order).
 */
export default function GridSettingsPopover({
  autoAdd, onAutoAddChange, side, onSideChange,
  journeysNotInViewCount, onAddAllJourneys,
  templatesNotInViewCount, onAddAllTemplates,
}: Props) {
  return (
    <Popover placement="bottom-end" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label="Grid settings"
          icon={<FiSettings />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          color="gray.600"
          h="26px"
          minH="26px"
          w="26px"
          minW="26px"
          _hover={{ bg: "gray.100", color: "gray.800" }}
          _active={{ bg: "gray.200" }}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="300px">
          <PopoverArrow />
          <PopoverBody p={4}>
            <VStack align="stretch" spacing={4}>
              <VStack align="stretch" spacing={1}>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel htmlFor="auto-add-new-hires" mb={0} fontSize="sm" fontWeight="medium">
                    Auto add new hires
                  </FormLabel>
                  <Switch
                    id="auto-add-new-hires"
                    size="sm"
                    isChecked={autoAdd}
                    onChange={(e) => onAutoAddChange(e.target.checked)}
                  />
                </FormControl>
                <CDS.Text10 color="gray.500">
                  When a new hire&apos;s journey kicks off (becomes active), drop their column into your view
                  automatically. Turn off to keep your view fixed — you can still add them manually with the
                  {" "}+ New hire button.
                </CDS.Text10>
              </VStack>

              <FormControl isDisabled={!autoAdd}>
                <FormLabel mb={1} fontSize="sm" fontWeight="medium">Add to</FormLabel>
                <RadioGroup value={side} onChange={(v) => onSideChange(v as AutoAddSide)}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="left" size="sm">Left</Radio>
                    <Radio value="right" size="sm">Right</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <Divider />

              <Button
                size="sm"
                variant="outline"
                onClick={onAddAllJourneys}
                isDisabled={journeysNotInViewCount === 0}
              >
                Add all active journeys{journeysNotInViewCount > 0 ? ` (${journeysNotInViewCount})` : ""}
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={onAddAllTemplates}
                isDisabled={templatesNotInViewCount === 0}
              >
                Add all task templates{templatesNotInViewCount > 0 ? ` (${templatesNotInViewCount})` : ""}
              </Button>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
